/**
 * EditorTopbarTools — botones del editor estilo Canva en la topbar del PlanBuilder.
 *
 * Expone:
 *   - "Estilo": panel de preset (Feature 3). Edita en vivo el `brand` del plan
 *     — paleta, modo claro/oscuro, tipografía display/body y densidad — y deja
 *     guardar/aplicar/borrar presets de estilo reutilizables del coach. Todo se
 *     persiste: el `brand` con el plan, los presets en la cuenta del coach.
 *   - "Iconos": modal con 23 iconos fitness con búsqueda. Click copia el SVG
 *     al clipboard para que el coach lo pegue donde quiera.
 */
import { useEffect, useMemo, useState } from "react";
import { Icon } from "../../lib/Icon";
import {
  ICON_CATALOG,
  ICON_CATEGORIES,
  LibraryIcon,
  searchIcons,
  type IconCategory,
  type IconEntry,
} from "../../lib/IconLibrary";
import {
  PHOSPHOR_CATALOG,
  PHOSPHOR_CATEGORIES,
  PHOSPHOR_WEIGHTS,
  searchPhosphor,
  type PhosphorEntry,
  type PhosphorIconCategory,
  type PhosphorWeight,
} from "../../lib/PhosphorIcons";
import { listAssetsForCoach, type AdminAsset } from "../../api/admin";
import { FONT_OPTIONS } from "../../lib/fontStack";
import type { PlanBrand, PlanDensity, StylePreset } from "../../types/plan";

export interface PaletteOption {
  id: string;
  name: string;
  primary: string;
  accent: string;
}

export const PALETTE_PRESETS: PaletteOption[] = [
  { id: "violet",   name: "Violeta",   primary: "#7c3aed", accent: "#ec4899" },
  { id: "neon",     name: "Neón",      primary: "#06ffa5", accent: "#ff006e" },
  { id: "pastel",   name: "Pastel",    primary: "#c084fc", accent: "#fda4af" },
  { id: "mono",     name: "Mono",      primary: "#18181b", accent: "#71717a" },
  { id: "tropical", name: "Tropical",  primary: "#06b6d4", accent: "#f97316" },
  { id: "heritage", name: "Heritage",  primary: "#78350f", accent: "#fbbf24" },
];

const DENSITY_OPTIONS: { id: PlanDensity; label: string }[] = [
  { id: "compact", label: "Compacto" },
  { id: "normal", label: "Normal" },
  { id: "spacious", label: "Amplio" },
];

// Defaults usados para leer el brand cuando un campo no está seteado.
const DEF = {
  primary: "#7c3aed",
  accent: "#ec4899",
  theme: "light" as const,
  displayFont: "bricolage",
  bodyFont: "inter",
  density: "normal" as PlanDensity,
};

interface EditorTopbarToolsProps {
  brand: PlanBrand | null;
  onBrandChange: (patch: Partial<PlanBrand>) => void;
  presets: StylePreset[];
  onPresetsChange: (presets: StylePreset[]) => void;
  toast: (msg: string) => void;
  /** Sección actualmente visible en el viewport (trackeada por el padre via
   *  IntersectionObserver). El picker la usa como destino implícito de los
   *  íconos — no hay dropdown. Es null si nada está visible. */
  activeSectionId: string | null;
  /** Inserta una decoración tipo Canva (ícono flotante) en la sección activa.
   *  Si no hay activa, el padre decide fallback. */
  onInsertDecoration: (iconId: string) => void;
}

export function EditorTopbarTools({
  brand,
  onBrandChange,
  presets,
  onPresetsChange,
  toast,
  activeSectionId,
  onInsertDecoration,
}: EditorTopbarToolsProps) {
  const [showStyle, setShowStyle] = useState(false);
  const [showIcons, setShowIcons] = useState(false);

  const primaryColor = brand?.primary || DEF.primary;
  const accentColor = brand?.accent || DEF.accent;

  // Cerrar con ESC cuando alguno está abierto
  useEffect(() => {
    if (!showStyle && !showIcons) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowStyle(false);
        setShowIcons(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showStyle, showIcons]);

  return (
    <>
      {/* Botón Estilo */}
      <div style={{ position: "relative" }}>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => {
            setShowStyle((v) => !v);
            setShowIcons(false);
          }}
          title="Editar el estilo del plan (paleta, tipografía, densidad, tema)"
        >
          <span
            style={{
              display: "inline-block",
              width: 12,
              height: 12,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${primaryColor} 50%, ${accentColor} 50%)`,
              marginRight: 6,
              border: "1px solid var(--border)",
            }}
          />
          Estilo
        </button>
        {showStyle && (
          <StylePanel
            brand={brand}
            onChange={onBrandChange}
            presets={presets}
            onPresetsChange={onPresetsChange}
            onClose={() => setShowStyle(false)}
            toast={toast}
          />
        )}
      </div>

      {/* Botón Iconos */}
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => {
          setShowIcons(true);
          setShowStyle(false);
        }}
        title="Insertar icono fitness"
      >
        <Icon name="sparkle" size={12} /> Iconos
      </button>

      {showIcons && (
        <IconPickerModal
          onClose={() => setShowIcons(false)}
          onInsert={(iconId) => {
            onInsertDecoration(iconId);
            // El padre resuelve cuál es la sección destino vía
            // activeSectionId trackeado con IntersectionObserver.
            toast("Ícono insertado — arrastralo donde quieras");
          }}
        />
      )}
    </>
  );
}

// ----------------------------------------------------------------------
// StylePanel — editor de preset de estilo (Feature 3)
// ----------------------------------------------------------------------

interface StylePanelProps {
  brand: PlanBrand | null;
  onChange: (patch: Partial<PlanBrand>) => void;
  presets: StylePreset[];
  onPresetsChange: (presets: StylePreset[]) => void;
  onClose: () => void;
  toast: (msg: string) => void;
}

function StylePanel({ brand, onChange, presets, onPresetsChange, onClose, toast }: StylePanelProps) {
  const primary = brand?.primary || DEF.primary;
  const accent = brand?.accent || DEF.accent;
  const theme = brand?.theme || DEF.theme;
  const displayFont = brand?.displayFont || DEF.displayFont;
  const bodyFont = brand?.bodyFont || DEF.bodyFont;
  const density = brand?.density || DEF.density;

  const [presetName, setPresetName] = useState("");

  const handleSavePreset = () => {
    const name = presetName.trim() || `Estilo ${presets.length + 1}`;
    const preset: StylePreset = {
      id: "preset_" + Date.now().toString(36),
      name,
      primary,
      accent,
      theme,
      displayFont,
      bodyFont,
      density,
    };
    onPresetsChange([...presets, preset]);
    setPresetName("");
    toast(`Preset "${name}" guardado`);
  };

  const handleApplyPreset = (p: StylePreset) => {
    onChange({
      primary: p.primary,
      accent: p.accent,
      theme: p.theme,
      displayFont: p.displayFont,
      bodyFont: p.bodyFont,
      density: p.density,
    });
    toast(`Preset "${p.name}" aplicado`);
  };

  const handleDeletePreset = (id: string) => {
    onPresetsChange(presets.filter((p) => p.id !== id));
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 70 }} />
      <div className="al-style-panel" role="dialog" aria-label="Editor de estilo">
        {/* Paleta */}
        <div className="al-style-section">
          <div className="al-pb-side-eyebrow">PALETA</div>
          <div className="al-style-palette-grid">
            {PALETTE_PRESETS.map((p) => {
              const active =
                p.primary.toLowerCase() === primary.toLowerCase() &&
                p.accent.toLowerCase() === accent.toLowerCase();
              return (
                <button
                  key={p.id}
                  type="button"
                  className={"al-style-swatch" + (active ? " is-active" : "")}
                  title={p.name}
                  onClick={() => onChange({ primary: p.primary, accent: p.accent })}
                >
                  <span
                    className="al-style-swatch-chip"
                    style={{ background: `linear-gradient(135deg, ${p.primary} 50%, ${p.accent} 50%)` }}
                  />
                  <span className="tiny">{p.name}</span>
                </button>
              );
            })}
          </div>
          <div className="al-style-custom">
            <label>
              <span className="tiny muted">Primario</span>
              <input
                type="color"
                value={primary}
                onChange={(e) => onChange({ primary: e.target.value })}
              />
            </label>
            <label>
              <span className="tiny muted">Acento</span>
              <input
                type="color"
                value={accent}
                onChange={(e) => onChange({ accent: e.target.value })}
              />
            </label>
          </div>
        </div>

        {/* Modo claro / oscuro */}
        <div className="al-style-section">
          <div className="al-pb-side-eyebrow">MODO</div>
          <div className="al-style-seg">
            <button
              type="button"
              className={theme === "light" ? "is-active" : ""}
              onClick={() => onChange({ theme: "light" })}
            >
              Claro
            </button>
            <button
              type="button"
              className={theme === "dark" ? "is-active" : ""}
              onClick={() => onChange({ theme: "dark" })}
            >
              Oscuro
            </button>
          </div>
        </div>

        {/* Tipografía — agrupada por categoría (Display / Body / Serif / Decorativa / Mono)
            con <optgroup> para que el coach navegue fácil entre 27 opciones. */}
        <div className="al-style-section">
          <div className="al-pb-side-eyebrow">TIPOGRAFÍA</div>
          <label className="al-style-field">
            <span className="tiny muted">Títulos</span>
            <select
              value={displayFont}
              onChange={(e) => onChange({ displayFont: e.target.value })}
            >
              {groupFontsByCategory(FONT_OPTIONS).map(([cat, opts]) => (
                <optgroup key={cat} label={cat}>
                  {opts.map((f) => (
                    <option key={f.id} value={f.id}>{f.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <label className="al-style-field">
            <span className="tiny muted">Cuerpo</span>
            <select
              value={bodyFont}
              onChange={(e) => onChange({ bodyFont: e.target.value })}
            >
              {groupFontsByCategory(FONT_OPTIONS).map(([cat, opts]) => (
                <optgroup key={cat} label={cat}>
                  {opts.map((f) => (
                    <option key={f.id} value={f.id}>{f.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
        </div>

        {/* Densidad */}
        <div className="al-style-section">
          <div className="al-pb-side-eyebrow">DENSIDAD</div>
          <div className="al-style-seg">
            {DENSITY_OPTIONS.map((d) => (
              <button
                key={d.id}
                type="button"
                className={density === d.id ? "is-active" : ""}
                onClick={() => onChange({ density: d.id })}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        {/* Mis presets */}
        <div className="al-style-section">
          <div className="al-pb-side-eyebrow">MIS PRESETS</div>
          {presets.length > 0 ? (
            <div className="al-style-presets">
              {presets.map((p) => (
                <div key={p.id} className="al-style-preset-row">
                  <button
                    type="button"
                    className="al-style-preset-apply"
                    onClick={() => handleApplyPreset(p)}
                    title={`Aplicar "${p.name}"`}
                  >
                    <span
                      className="al-style-swatch-chip sm"
                      style={{ background: `linear-gradient(135deg, ${p.primary} 50%, ${p.accent} 50%)` }}
                    />
                    <span className="al-style-preset-name">{p.name}</span>
                  </button>
                  <button
                    type="button"
                    className="al-style-preset-del"
                    onClick={() => handleDeletePreset(p.id)}
                    title="Borrar preset"
                  >
                    <Icon name="x" size={11} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="tiny muted">Todavía no guardaste presets.</div>
          )}
          <div className="al-style-save">
            <input
              type="text"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="Nombre del preset…"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSavePreset();
              }}
            />
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleSavePreset}
            >
              <Icon name="check" size={11} /> Guardar
            </button>
          </div>
        </div>

        <div className="tiny muted" style={{ lineHeight: 1.4 }}>
          Los cambios se ven al instante y se guardan con el plan. Los presets
          quedan disponibles en todos tus planes.
        </div>
      </div>
    </>
  );
}

// ----------------------------------------------------------------------
// IconPickerModal — picker tipo Canva con ~110 íconos categorizados.
// Reemplaza el viejo FitnessIconPickerModal que copiaba SVG al clipboard
// (workflow que en práctica no servía porque los InlineEditable no parsean
// SVG-paste). Ahora INSERTA el ícono como decoración flotante draggable
// en la sección que elija el coach.
// ----------------------------------------------------------------------

interface IconPickerModalProps {
  onClose: () => void;
  /** Inserta el ícono en la sección actualmente visible (el padre la
   *  tracquea via IntersectionObserver). */
  onInsert: (iconId: string) => void;
}

/** Librería activa del picker. Tres opciones:
 *   - phosphor: 200+ premium con 4 weights (default)
 *   - lucide: 110 outline-only legacy
 *   - nilabs: assets custom subidos por admin (íconos + ilustraciones)
 */
type IconLib = "phosphor" | "lucide" | "nilabs";

function IconPickerModal({ onClose, onInsert }: IconPickerModalProps) {
  const [q, setQ] = useState("");
  const [lib, setLib] = useState<IconLib>("phosphor");
  const [phWeight, setPhWeight] = useState<PhosphorWeight>("regular");
  const [lucideCategory, setLucideCategory] = useState<IconCategory | "Todos">("Todos");
  const [phCategory, setPhCategory] = useState<PhosphorIconCategory | "Todos">("Todos");
  const [nilabsCategory, setNilabsCategory] = useState<string>("Todos");

  // Lazy-load de assets Nilabs (admin) — solo al primer render del picker
  const [nilabsAssets, setNilabsAssets] = useState<AdminAsset[] | null>(null);
  const [nilabsErr, setNilabsErr] = useState<string | null>(null);
  useEffect(() => {
    listAssetsForCoach()
      .then(setNilabsAssets)
      .catch((e) => setNilabsErr(e instanceof Error ? e.message : String(e)));
  }, []);

  // Filtrado para Lucide
  const filteredLucide = useMemo<IconEntry[]>(() => {
    let list = q.trim() ? searchIcons(q) : ICON_CATALOG;
    if (lucideCategory !== "Todos") list = list.filter((e) => e.category === lucideCategory);
    return list;
  }, [q, lucideCategory]);

  // Filtrado para Phosphor
  const filteredPhosphor = useMemo<PhosphorEntry[]>(() => {
    let list = q.trim() ? searchPhosphor(q) : PHOSPHOR_CATALOG;
    if (phCategory !== "Todos") list = list.filter((e) => e.category === phCategory);
    return list;
  }, [q, phCategory]);

  // Filtrado para Nilabs (assets custom)
  const filteredNilabs = useMemo<AdminAsset[]>(() => {
    if (!nilabsAssets) return [];
    const query = q.trim().toLowerCase();
    return nilabsAssets.filter((a) => {
      if (nilabsCategory !== "Todos" && a.category !== nilabsCategory) return false;
      if (query) {
        const hay = `${a.name.toLowerCase()} ${a.tags || ""} ${a.category || ""}`;
        if (!hay.includes(query)) return false;
      }
      return true;
    });
  }, [nilabsAssets, nilabsCategory, q]);

  const nilabsCategories = useMemo(() => {
    if (!nilabsAssets) return [] as string[];
    const set = new Set<string>();
    for (const a of nilabsAssets) if (a.category) set.add(a.category);
    return Array.from(set).sort();
  }, [nilabsAssets]);

  const isPh = lib === "phosphor";
  const isNi = lib === "nilabs";
  const filteredCount = isPh ? filteredPhosphor.length : isNi ? filteredNilabs.length : filteredLucide.length;
  const activeCategory = isPh ? phCategory : isNi ? nilabsCategory : lucideCategory;
  const categories: string[] = isPh ? PHOSPHOR_CATEGORIES : isNi ? nilabsCategories : ICON_CATEGORIES;
  const setActiveCategory = (c: string) => {
    if (isPh) setPhCategory(c as PhosphorIconCategory | "Todos");
    else if (isNi) setNilabsCategory(c);
    else setLucideCategory(c as IconCategory | "Todos");
  };

  return (
    <div className="al-modal-backdrop" onClick={onClose}>
      <div
        className="al-modal al-icon-picker"
        role="dialog"
        aria-label="Insertar ícono"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="al-modal-hd">
          <div>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
              Insertar ícono
            </h3>
            <div className="tiny muted" style={{ marginTop: 2 }}>
              Clickeá el ícono y aparece en la hoja que estás viendo. Desde ahí lo
              arrastrás, redimensionás (esquina) y rotás (palanca de arriba) como
              Canva. Esc/Del lo eliminan.
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Cerrar">
            <Icon name="x" size={16} />
          </button>
        </div>

        <div className="al-icon-picker-body">
          {/* Tabs Phosphor / Lucide / Nilabs */}
          <div className="al-icon-picker-libs">
            <button
              type="button"
              className={"al-icon-picker-lib" + (isPh ? " is-active" : "")}
              onClick={() => setLib("phosphor")}
            >
              Phosphor <span className="al-icon-picker-lib-count">{PHOSPHOR_CATALOG.length}</span>
            </button>
            <button
              type="button"
              className={"al-icon-picker-lib" + (lib === "lucide" ? " is-active" : "")}
              onClick={() => setLib("lucide")}
            >
              Lucide <span className="al-icon-picker-lib-count">{ICON_CATALOG.length}</span>
            </button>
            <button
              type="button"
              className={"al-icon-picker-lib" + (isNi ? " is-active" : "")}
              onClick={() => setLib("nilabs")}
              title="Assets curados por el equipo Nilabs"
            >
              ⭐ Nilabs <span className="al-icon-picker-lib-count">
                {nilabsAssets ? nilabsAssets.length : "…"}
              </span>
            </button>
            <span className="muted tiny" style={{ marginLeft: "auto" }}>
              {isPh ? "Estilo del ícono:" : ""}
            </span>
            {isPh && (
              <div className="al-icon-picker-weights">
                {PHOSPHOR_WEIGHTS.map((w) => (
                  <button
                    key={w.id}
                    type="button"
                    className={"al-icon-picker-weight" + (phWeight === w.id ? " is-active" : "")}
                    onClick={() => setPhWeight(w.id)}
                    title={`Renderizar todos los íconos en estilo ${w.label.toLowerCase()}`}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Buscador */}
          <div className="al-icon-picker-toolbar">
            <div className="al-icon-picker-search">
              <Icon name="search" size={14} />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={isPh
                  ? "Buscar barra, anatomía, fruta, trofeo…"
                  : "Buscar mancuerna, comida, reloj, trofeo…"}
                autoFocus
              />
              {q && (
                <button className="link-btn" onClick={() => setQ("")} type="button">
                  limpiar
                </button>
              )}
            </div>
          </div>

          {/* Chips de categoría — cambian según la librería activa */}
          <div className="al-icon-picker-cats">
            <button
              type="button"
              className={"al-icon-picker-cat" + (activeCategory === "Todos" ? " is-active" : "")}
              onClick={() => setActiveCategory("Todos")}
            >
              Todos
            </button>
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                className={"al-icon-picker-cat" + (activeCategory === c ? " is-active" : "")}
                onClick={() => setActiveCategory(c)}
              >
                {c}
              </button>
            ))}
          </div>

          {/* Grid: Phosphor / Lucide / Nilabs según tab activa */}
          {isNi && nilabsErr ? (
            <div className="muted" style={{ textAlign: "center", padding: 24 }}>
              No pude cargar los assets de Nilabs: {nilabsErr}
            </div>
          ) : isNi && nilabsAssets == null ? (
            <div className="muted" style={{ textAlign: "center", padding: 24 }}>
              Cargando assets Nilabs…
            </div>
          ) : filteredCount === 0 ? (
            <div className="muted" style={{ textAlign: "center", padding: 32 }}>
              {isNi && (nilabsAssets?.length ?? 0) === 0
                ? "Todavía no hay assets de Nilabs. El equipo está curando — volvé pronto."
                : `Sin resultados ${q ? `para "${q}"` : `en ${activeCategory}`}.`}
            </div>
          ) : (
            <div className="al-icon-picker-grid">
              {isPh && filteredPhosphor.map((entry) => (
                <button
                  key={entry.id}
                  className="al-icon-picker-cell"
                  type="button"
                  onClick={() => {
                    // Storage: "ph:<id>:<weight>" para que el renderer
                    // sepa qué librería y qué estilo.
                    onInsert(`ph:${entry.id}:${phWeight}`);
                    onClose();
                  }}
                  title={`Insertar ${entry.label} en estilo ${phWeight}`}
                >
                  <LibraryIcon id={`ph:${entry.id}:${phWeight}`} size={28} />
                  <span className="tiny">{entry.label}</span>
                </button>
              ))}
              {lib === "lucide" && filteredLucide.map((entry) => (
                <button
                  key={entry.id}
                  className="al-icon-picker-cell"
                  type="button"
                  onClick={() => {
                    onInsert(entry.id);
                    onClose();
                  }}
                  title={`Insertar ${entry.label} en la hoja visible`}
                >
                  <LibraryIcon id={entry.id} size={28} />
                  <span className="tiny">{entry.label}</span>
                </button>
              ))}
              {isNi && filteredNilabs.map((asset) => (
                <button
                  key={asset.id}
                  className="al-icon-picker-cell"
                  type="button"
                  onClick={() => {
                    // Storage: "asset:<id>" — el renderer hace fetch del SVG/PNG.
                    onInsert(`asset:${asset.id}`);
                    onClose();
                  }}
                  title={`Insertar ${asset.name}`}
                >
                  <img
                    src={asset.url}
                    alt={asset.name}
                    style={{ width: 28, height: 28, objectFit: "contain" }}
                    loading="lazy"
                  />
                  <span className="tiny">{asset.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Agrupa el array plano FONT_OPTIONS por `category` preservando el orden de
 * aparición (Display antes que Body antes que Serif, etc.). Devuelve un
 * array de tuplas [categoría, opciones[]] para iterar fácil en <optgroup>.
 */
function groupFontsByCategory<T extends { category: string }>(
  options: T[],
): Array<[string, T[]]> {
  const map = new Map<string, T[]>();
  for (const opt of options) {
    if (!map.has(opt.category)) map.set(opt.category, []);
    map.get(opt.category)!.push(opt);
  }
  return Array.from(map.entries());
}
